import { isInwardSupplyRow } from "../../../services/billingImportFindService";
import {
  lineSaleAmount,
  loadLineChunkRowsForDateRange,
} from "./salesLineChunksReader";
import {
  normalizeSalesImportBranchId,
  salesImportBranchDisplayName,
  sortSalesImportBranchIds,
} from "../config/salesImportBranchIds";
import { rowTranDateYmd } from "../utils/dateParse";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function branchIdFromRow(row) {
  return normalizeSalesImportBranchId(row.branchId ?? row.BranchID ?? row.branch ?? "") || "Unknown";
}

function monthKeyFromRow(row) {
  const ymd = rowTranDateYmd(row);
  if (!ymd || ymd.length < 7) return "";
  return ymd.slice(0, 7);
}

/**
 * @param {object[]} rows
 * @param {{ year?: number }} [opts]
 */
export function aggregateBranchMonthlySalesPurchase(rows, { year } = {}) {
  const monthKeys = new Set();
  const cellMap = new Map();

  for (const row of rows || []) {
    const mk = monthKeyFromRow(row);
    if (!mk) continue;
    const y = Number(mk.slice(0, 4));
    if (year != null && Number.isFinite(year) && y !== year) continue;

    monthKeys.add(mk);
    const branch = branchIdFromRow(row);
    const cellKey = `${branch}::${mk}`;
    const prev = cellMap.get(cellKey) || {
      branch,
      monthKey: mk,
      salesAmount: 0,
      purchaseAmount: 0,
      salesLines: 0,
      purchaseLines: 0,
    };

    const amt = lineSaleAmount(row);
    if (isInwardSupplyRow(row)) {
      prev.purchaseAmount += amt;
      prev.purchaseLines += 1;
    } else {
      prev.salesAmount += amt;
      prev.salesLines += 1;
    }
    cellMap.set(cellKey, prev);
  }

  const sortedMonths = [...monthKeys].sort();
  const branchSet = new Set();
  for (const c of cellMap.values()) branchSet.add(c.branch);
  const branches = sortSalesImportBranchIds([...branchSet]);

  const tableRows = branches.map((branch) => {
    const months = sortedMonths.map((mk) => {
      const c = cellMap.get(`${branch}::${mk}`) || {
        salesAmount: 0,
        purchaseAmount: 0,
        salesLines: 0,
        purchaseLines: 0,
      };
      return {
        monthKey: mk,
        label: formatMonthLabel(mk),
        salesAmount: c.salesAmount,
        purchaseAmount: c.purchaseAmount,
        salesLines: c.salesLines,
        purchaseLines: c.purchaseLines,
      };
    });
    const yearSales = months.reduce((s, m) => s + m.salesAmount, 0);
    const yearPurchase = months.reduce((s, m) => s + m.purchaseAmount, 0);
    return {
      branch,
      branchLabel: salesImportBranchDisplayName(branch),
      months,
      yearSales,
      yearPurchase,
      yearNet: yearSales - yearPurchase,
    };
  });

  const monthTotals = sortedMonths.map((mk) => {
    let salesAmount = 0;
    let purchaseAmount = 0;
    for (const br of branches) {
      const c = cellMap.get(`${br}::${mk}`);
      if (c) {
        salesAmount += c.salesAmount;
        purchaseAmount += c.purchaseAmount;
      }
    }
    return { monthKey: mk, label: formatMonthLabel(mk), salesAmount, purchaseAmount };
  });

  const grandSales = monthTotals.reduce((s, m) => s + m.salesAmount, 0);
  const grandPurchase = monthTotals.reduce((s, m) => s + m.purchaseAmount, 0);

  return {
    branches: tableRows,
    months: sortedMonths.map((mk) => ({ monthKey: mk, label: formatMonthLabel(mk) })),
    monthTotals,
    grandSales,
    grandPurchase,
    grandNet: grandSales - grandPurchase,
    lineCount: (rows || []).length,
  };
}

export function formatMonthLabel(monthKey) {
  const [y, m] = String(monthKey || "").split("-");
  const mi = Number(m) - 1;
  if (!y || mi < 0 || mi > 11) return monthKey || "—";
  return `${MONTH_LABELS[mi]} ${y}`;
}

/**
 * Load import lines for a calendar year and aggregate by branch + month.
 * @param {number} year
 * @param {(n: number) => void} [onProgress]
 */
export async function fetchBranchMonthlySalesPurchaseReport(year, onProgress) {
  const y = Number(year);
  if (!Number.isFinite(y)) throw new Error("Select a valid year.");

  const fromDate = `${y}-01-01`;
  const toDate = `${y}-12-31`;
  const { rows } = await loadLineChunkRowsForDateRange({ fromDate, toDate }, (n) => {
    onProgress?.(n);
  });

  return {
    ...aggregateBranchMonthlySalesPurchase(rows, { year: y }),
    year: y,
    fromDate,
    toDate,
  };
}

/** Flat rows for Excel export. */
export function flattenBranchMonthlyForExport(report) {
  const out = [];
  for (const br of report?.branches || []) {
    const row = {
      BranchID: br.branch,
      Branch: br.branchLabel || br.branch,
      "Year Sales": br.yearSales,
      "Year Purchase": br.yearPurchase,
      "Year Net (Sales − Purchase)": br.yearNet,
    };
    for (const m of br.months || []) {
      row[`${m.label} Sales`] = m.salesAmount;
      row[`${m.label} Purchase`] = m.purchaseAmount;
    }
    out.push(row);
  }
  return out;
}
